/* eslint-disable @typescript-eslint/no-explicit-any */
import { api, APIError } from "../../../lib/api";
import { sendToTerminal } from "../../../lib/terminalLogger";

export interface ExportProfile {
	id: number;
	name: string;
	description?: string;
	options?: Record<string, any>;
}

export interface ExportRulesResponse {
	profiles: ExportProfile[];
}

/**
 * Adapter: Converte resposta FLAT da API para formato NESTED esperado
 *
 * API retorna (FLAT):
 * {
 *   profile: { name, description },
 *   tiff: { campos TIFF },
 *   png: { campos PNG },
 *   pdf: { campos PDF }
 * }
 *
 * Frontend espera (NESTED):
 * {
 *   profiles: [{
 *     id: 1,
 *     name: "...",
 *     options: {
 *       tiff: { campos TIFF },
 *       png: { campos PNG },
 *       pdf: { campos PDF }
 *     }
 *   }]
 * }
 */
function adaptFlatToNested(data: any): ExportRulesResponse {
	sendToTerminal("🔄 [API:ADAPTER_START]", {
		hasProfiles: !!data.profiles,
		hasTiff: !!data.tiff,
		hasPng: !!data.png,
		hasPdf: !!data.pdf,
		hasEps: !!data.eps,
		hasProfile: !!data.profile,
	});

	// Se já está no formato nested, retornar como está
	if (Array.isArray(data?.profiles)) {
		sendToTerminal("✅ [API:ADAPTER_SKIP]", { reason: "already-nested" });
		return { profiles: data.profiles };
	}

	// Converter formato FLAT para NESTED
	const profile: ExportProfile = {
		id: 1,
		name: data.profile?.name || "Default Profile",
		description: data.profile?.description || "Perfil de exportação",
		options: {
			tiff: data.tiff || {},
			png: data.png || {},
			pdf: data.pdf || {},
			eps: data.eps || {},
		},
	};

	sendToTerminal("✅ [API:ADAPTER_COMPLETE]", {
		profileName: profile.name,
		hasOptions: !!profile.options,
		formats: Object.keys(profile.options || {}),
		tiffFields: profile.options?.tiff
			? Object.keys(profile.options.tiff).length
			: 0,
		pngFields: profile.options?.png
			? Object.keys(profile.options.png).length
			: 0,
		pdfFields: profile.options?.pdf
			? Object.keys(profile.options.pdf).length
			: 0,
		epsFields: profile.options?.eps
			? Object.keys(profile.options.eps).length
			: 0,
	});

	return { profiles: [profile] };
}

/**
 * Busca perfis de exportação para PRODUÇÃO
 * @param profileId - ID do perfil (default: 1)
 */
export async function fetchExportRules(
	profileId = 1,
): Promise<ExportRulesResponse> {
	const endpoint = `/cdr/rules/producao/perfil/${profileId}`;

	try {
		sendToTerminal("🌐 [API:FETCH_RULES]", { endpoint, profileId });

		// Rota correta para PRODUÇÃO
		const data: any = await api(endpoint, { method: "GET" });

		sendToTerminal("📥 [API:RESPONSE_RECEIVED]", {
			hasData: !!data,
			keys: data ? Object.keys(data) : [],
			hasTiff: !!data?.tiff,
			hasPng: !!data?.png,
			hasPdf: !!data?.pdf,
			hasEps: !!data?.eps,
		});

		// Aplicar adapter para garantir formato consistente
		return adaptFlatToNested(data);
	} catch (err) {
		sendToTerminal("❌ [API:FETCH_RULES_ERROR]", {
			endpoint,
			profileId,
			error: err instanceof Error ? err.message : String(err),
			status: err instanceof APIError ? err.status : "unknown",
		});

		// NÃO fazer fallback - deixar erro subir
		throw err;
	}
}
